import * as Yup from 'yup';

import { AllocationType, Strategy, StrategyType } from '@ndvr/portfolio-api';

import { createTotalWeightTest, createUniqueNameTest } from '../../shared-utils';

export const createFormSchema = (
    existingAllocationNames: string[] = [],
    currentAllocationName?: string,
    availableStrategies: Strategy[] = [],
) =>
    Yup.object({
        allocationName: Yup.string()
            .required('Allocation name is required')
            .trim()
            .min(1, 'Allocation name cannot be empty')
            .max(100, 'Allocation name cannot exceed 100 characters')
            .test(
                'unique-name',
                'An allocation with this name already exists',
                createUniqueNameTest(existingAllocationNames, currentAllocationName),
            ),
        allocationGroupName: Yup.string()
            .required('Allocation group name is required')
            .trim()
            .min(1, 'Allocation group name cannot be empty')
            .max(100, 'Allocation group name cannot exceed 100 characters'),
        allocationType: Yup.string()
            .oneOf([AllocationType.PERSONAL, AllocationType.SHARED])
            .required('Allocation type is required'),
        strategyAllocations: Yup.array()
            .of(
                Yup.object({
                    strategyGUID: Yup.string().required(),
                    weight: Yup.string()
                        .required('Weight is required')
                        .test('is-positive', 'Weight must be greater than 0', function (value) {
                            const numValue = parseFloat(value || '0');
                            return !isNaN(numValue) && numValue > 0;
                        }),
                }),
            )
            .default([])
            .min(1, 'At least one strategy is required')
            .test('total-weight', 'Total weight must equal 100%', createTotalWeightTest())
            .test(
                'no-personal-strategies-in-shared',
                'Shared allocations cannot contain personal strategies',
                function (value) {
                    const allocationType = this.parent.allocationType;
                    if (allocationType !== AllocationType.SHARED) {
                        return true;
                    }
                    if (!value || value.length === 0) {
                        return true;
                    }
                    const hasPersonalStrategy = value.some((strategyAllocation) => {
                        const strategy = availableStrategies.find(
                            (s) => s.strategyGUID === strategyAllocation.strategyGUID,
                        );
                        return strategy?.strategyType === StrategyType.PERSONAL;
                    });
                    return !hasPersonalStrategy;
                },
            ),
    });
